import type { FeatureKey } from '@gbv/types';
import { NAV_SECTIONS } from '../navigation';

/**
 * What the assistant knows.
 *
 * No model and no network call. This is a searchable index of the product,
 * and the honesty of that matters: it can find a screen, explain a rule, and
 * hand you a link, and it says plainly when it cannot help rather than
 * inventing something that sounds like an answer.
 *
 * Two halves.
 *
 *   Derived — every entry in the navigation becomes a "where is X" answer
 *   automatically. Adding a screen to the sidebar adds it here, so the
 *   assistant cannot fall behind the product the way a hand-written help page
 *   does.
 *
 *   Written — the questions that are about a rule rather than a location.
 *   "Why can I not delete this" has no screen to point at; it needs the rule
 *   explained and a next step.
 */

export interface Answer {
  id: string;
  /** Shown as the heading of the reply. */
  title: string;
  /** The reply itself. Kept to a few sentences; this is a chat, not a manual. */
  body: string;
  /** Words a person might actually type. Matched against, never displayed. */
  keywords: string[];
  /** Offered as a button under the reply. */
  action?: { label: string; to: string };
  /** Hidden unless the person's membership grants this. */
  feature?: FeatureKey;
  /** Follow-ups offered as chips after this answer. */
  related?: string[];
}

/** Every screen in the sidebar, as an answer about where to find it. */
function fromNavigation(): Answer[] {
  const out: Answer[] = [];

  for (const section of NAV_SECTIONS) {
    for (const group of section.groups) {
      for (const item of group.items) {
        const where = group.department
          ? `${group.department}, under ${section.label}`
          : section.label;

        out.push({
          id: `nav:${item.to}`,
          title: item.label,
          body: item.planned
            ? `${item.label} is on the map but not built yet. It appears in the sidebar under ${where} with a "soon" marker so you can see it is coming.`
            : `${item.label} is in the left sidebar under ${where}.`,
          keywords: [
            ...tokenise(item.label),
            ...tokenise(group.department ?? ''),
            item.to.replace('/', ''),
          ],
          action: item.planned ? undefined : { label: `Open ${item.label}`, to: item.to },
          feature: item.feature,
        });
      }
    }
  }

  return out;
}

/**
 * The rules and how-tos. Written by hand because they are not about where a
 * screen is — they are about what the product does and why it refuses things.
 */
const WRITTEN: Answer[] = [
  {
    id: 'lead-new',
    title: 'Adding a lead',
    body: 'Leads → New lead. Name and phone are the only required fields. The moment you save it, the person appears on Contacts and the firm on Companies — you do not have to press Convert for that. Convert is for when there is a deal worth tracking.',
    keywords: ['add', 'new', 'create', 'lead', 'enquiry', 'prospect', 'record'],
    action: { label: 'Open Leads', to: '/leads' },
    feature: 'crm',
    related: ['lead-import', 'lead-convert', 'crm-sync'],
  },
  {
    id: 'lead-import',
    title: 'Importing leads',
    body: 'Leads → Import. Upload a CSV, map the columns, check the counts on the preview, then commit. Only name and phone are required; a row with an unreadable stage still imports. Imported rows now appear on Contacts and Companies too.',
    keywords: ['import', 'upload', 'csv', 'excel', 'bulk', 'file', 'spreadsheet', 'mass', 'many'],
    action: { label: 'Open Leads', to: '/leads' },
    feature: 'dataImport',
    related: ['crm-sync', 'lead-export'],
  },
  {
    id: 'crm-sync',
    title: 'Leads not showing on Contacts or Companies',
    body: 'New leads and new imports appear on both pages automatically. Leads added before that change need a one-off backfill, which an admin runs — ask them to run the CRM backfill for your client. Matching is by phone number for people and by normalised name for firms, so "Sharma Motors" and "sharma motors pvt ltd" stay one account.',
    keywords: [
      'sync',
      'missing',
      'not',
      'showing',
      'appear',
      'contacts',
      'companies',
      'backfill',
      'empty',
    ],
    related: ['lead-import', 'contact-search'],
  },
  {
    id: 'lead-convert',
    title: 'Converting a lead',
    body: 'Open the lead and press Convert. That creates the deal in the pipeline and links it to the contact and company. If the contact already exists it is reused rather than duplicated — one number, one person.',
    keywords: [
      'convert',
      'deal',
      'opportunity',
      'pipeline',
      'qualify',
      'won',
      'customer',
      'turn',
      'become',
      'lead',
    ],
    feature: 'crm',
    related: ['lead-new'],
  },
  {
    id: 'lead-export',
    title: 'Exporting records',
    body: 'Every list — Leads, Contacts, Companies — has an Export button that downloads what is on screen, filters included. Exporting while a filter is applied gives you the filtered set, not the whole book.',
    keywords: [
      'export',
      'download',
      'csv',
      'report',
      'extract',
      'backup',
      'lead',
      'contact',
      'company',
      'excel',
    ],
    feature: 'dataExport',
  },
  {
    id: 'bulk-delete',
    title: 'Deleting several records at once',
    body: 'Tick the rows on Leads, Contacts or Companies and press Delete. A company that still has contacts or deals attached is kept rather than removed, and the app tells you which ones and why.',
    keywords: [
      'delete',
      'remove',
      'bulk',
      'several',
      'many',
      'clear',
      'tick',
      'select',
      'multiple',
      'lead',
      'contact',
      'company',
      'row',
      'record',
    ],
    related: ['delete-refused'],
  },
  {
    id: 'delete-refused',
    title: 'Why you cannot delete something',
    body: 'Deleting is a separate permission from working on a record. Nobody below admin can remove a task, file, note or record until an admin grants that area to them. If the Delete button is missing or you get "Only an admin can delete this", ask an admin to grant the delete permission for that area on your account.',
    keywords: [
      'cannot',
      'cant',
      'delete',
      'permission',
      'denied',
      'refused',
      'blocked',
      'admin',
      'forbidden',
      'access',
    ],
    related: ['bulk-delete', 'permissions'],
  },
  {
    id: 'permissions',
    title: 'What you can reach, and who decides',
    body: 'An admin sets your capabilities per client, on your account in the admin console. That is why the same person can run the dialer for one client and be read-only on another. If a screen is missing from your sidebar entirely, you have not been granted it — a sidebar of locked doors is worse than a short one.',
    keywords: [
      'permission',
      'access',
      'cannot',
      'see',
      'missing',
      'hidden',
      'capability',
      'role',
      'rights',
      'grant',
      'allow',
      'able',
      'allowed',
    ],
  },
  {
    id: 'drive',
    title: 'Where client files live',
    body: 'Drive, under Operations & Support. It opens on folders — catalogues, proposals, designs, contracts and the rest — with the files underneath. Your client sees the same shelf minus anything marked internal, and can add to it themselves.',
    keywords: [
      'file',
      'files',
      'drive',
      'document',
      'storage',
      'catalogue',
      'catalog',
      'proposal',
      'design',
      'brochure',
      'upload',
      'folder',
      'attachment',
    ],
    action: { label: 'Open Drive', to: '/drive' },
    feature: 'assets',
    related: ['drive-access'],
  },
  {
    id: 'drive-access',
    title: 'Controlling who sees a file',
    body: "Each file has two settings — what the client can do with it, and what staff can do with it — set by an admin on the client's Vault page. A signed contract can be view-only for everyone; an internal margin sheet can be hidden from the client entirely. On Drive you can toggle a file as internal, which hides it from the client.",
    keywords: [
      'access',
      'share',
      'permission',
      'internal',
      'confidential',
      'private',
      'hide',
      'client',
      'see',
      'lock',
    ],
    feature: 'assets',
    related: ['drive', 'permissions'],
  },
  {
    id: 'call-log',
    title: 'Recording a call or an email',
    body: "Open the lead and use the Call logs or Mail logs tab. Most calls happen on your own handset and most mail leaves from your own client, so this is how the record exists at all. Duration is entered as minutes and seconds, and the lead's own number fills in for you.",
    keywords: [
      'call',
      'log',
      'record',
      'phone',
      'rang',
      'mail',
      'email',
      'sent',
      'contacted',
      'note',
      'history',
    ],
    feature: 'crm',
    related: ['lead-new'],
  },
  {
    id: 'task-assign',
    title: 'Tasks and who they belong to',
    body: "The task board shows everyone's tasks with the assignee on each card, and who assigned it. Only the person a task is assigned to gets the notification. The panel on the right of your workspace shows your own tasks and reminders, nobody else's.",
    keywords: ['task', 'assign', 'assigned', 'todo', 'board', 'work', 'who', 'reopen', 'edit'],
    action: { label: 'Open Tasks', to: '/internal/tasks' },
    related: ['reminder-alerts'],
  },
  {
    id: 'reminder-alerts',
    title: 'Reminders and deadline alerts',
    body: 'A task or reminder with a due time warns you three times: five minutes before, one minute before, and at the moment itself. The last one is a dialog you have to dismiss. A sound is optional and off by default — turn it on in Settings.',
    keywords: [
      'reminder',
      'alert',
      'due',
      'deadline',
      'notification',
      'popup',
      'sound',
      'notify',
      'warn',
      'alarm',
    ],
    action: { label: 'Open Settings', to: '/account' },
    related: ['task-assign', 'notifications'],
  },
  {
    id: 'notifications',
    title: 'Notifications and sound',
    body: 'Settings has two switches. Desktop notifications appear only when this tab is in the background — with the app on screen it tells you here instead, so you never get both. The notification sound is separate and off by default, since plenty of people work in a room with other people in it.',
    keywords: [
      'notification',
      'sound',
      'chime',
      'alert',
      'desktop',
      'popup',
      'silent',
      'mute',
      'noise',
    ],
    action: { label: 'Open Settings', to: '/account' },
  },
  {
    id: 'sessions',
    title: 'Devices signed in to your account',
    body: 'Settings → Where you are signed in. One line per browser or phone, with the one you are on marked. Press Sign out on anything you do not recognise and it stops working immediately — you do not need an admin for that.',
    keywords: [
      'device',
      'session',
      'login',
      'signed',
      'sign',
      'out',
      'logout',
      'security',
      'phone',
      'lost',
      'stolen',
      'browser',
      'kick',
    ],
    action: { label: 'Open Settings', to: '/account' },
  },
  {
    id: 'password',
    title: 'Changing your password or PIN',
    body: 'Settings → Password. Changing it signs you out everywhere else, including any phone still logged in. The quick-unlock PIN is separate and works only on browsers you set it up on.',
    keywords: ['password', 'pin', 'change', 'reset', 'forgot', 'unlock', 'login', 'security'],
    action: { label: 'Open Settings', to: '/account' },
  },
  {
    id: 'office',
    title: 'Writing documents and spreadsheets',
    body: 'Office, under Operations & Support. Notes, documents and spreadsheets that save as you type. A document or note exports as .docx and a sheet as .csv.',
    keywords: [
      'office',
      'document',
      'doc',
      'word',
      'excel',
      'sheet',
      'spreadsheet',
      'note',
      'write',
      'editor',
    ],
    action: { label: 'Open Office', to: '/office' },
    feature: 'assets',
  },
  {
    id: 'invoice',
    title: 'Invoices, quotes and proposals',
    body: "Quotes and proposals are under Sales & Revenue; tax invoices under Finance & Legal. Numbering follows your client's own prefix. A document needing approval shows up in the same list.",
    keywords: [
      'invoice',
      'bill',
      'quote',
      'quotation',
      'proposal',
      'gst',
      'tax',
      'payment',
      'money',
      'estimate',
      'send',
      'raise',
      'issue',
    ],
    action: { label: 'Open Quotes & proposals', to: '/documents' },
    feature: 'invoicingView',
  },
  {
    id: 'chat',
    title: 'Messaging colleagues and clients',
    body: 'The chat button at the bottom right has two sides. Official carries the team channel and the client conversation; Personal is a private one-to-one with a colleague that nobody else sees. Messages arrive as they are sent.',
    keywords: [
      'chat',
      'message',
      'talk',
      'colleague',
      'team',
      'personal',
      'private',
      'dm',
      'whatsapp',
      'client',
    ],
    related: ['notifications'],
  },
  {
    id: 'version',
    title: 'Which version you are running',
    body: 'Settings → About this workspace. It shows the build the server is running, whether the page you have open is still current, and what changed in the latest release.',
    keywords: [
      'version',
      'update',
      'build',
      'release',
      'new',
      'changed',
      'about',
      'upgrade',
      'latest',
    ],
    action: { label: 'Open Settings', to: '/account' },
  },
  {
    id: 'contact-search',
    title: 'Finding a person or a company',
    body: 'Contacts and Companies both search by name, and Contacts also searches by phone number and email — type the number without the +91 and it still finds them. Leads search the same way.',
    keywords: [
      'search',
      'find',
      'look',
      'contact',
      'person',
      'company',
      'number',
      'phone',
      'lookup',
      'who',
    ],
    action: { label: 'Open Contacts', to: '/people' },
    feature: 'crm',
    related: ['crm-sync'],
  },
];

/** Small, high-frequency words carry no signal and are dropped before matching. */
export const STOPWORDS = new Set([
  'a',
  'not',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'but',
  'by',
  'can',
  'do',
  'does',
  'for',
  'from',
  'get',
  'has',
  'have',
  'how',
  'i',
  'in',
  'is',
  'it',
  'its',
  'me',
  'my',
  'of',
  'on',
  'or',
  'that',
  'the',
  'this',
  'to',
  'was',
  'what',
  'when',
  'where',
  'which',
  'why',
  'will',
  'with',
  'you',
  'your',
  'please',
  'want',
  'need',
  'would',
  'could',
  'see',
  'show',
  'find',
  'open',
  'should',
  'am',
]);

/**
 * Words people type for the words the product uses.
 *
 * The single highest-value part of matching without a model: somebody asks
 * about a "bill" and the answer is filed under "invoice". One hop covers most
 * of the distance between how people talk and how a product is labelled.
 */
export const SYNONYMS: Record<string, string[]> = {
  bill: ['invoice'],
  billing: ['invoice'],
  customer: ['contact', 'client', 'person'],
  customers: ['contact', 'client', 'person'],
  people: ['contact', 'person'],
  folder: ['drive', 'file'],
  attachment: ['file'],
  doc: ['document'],
  docs: ['document'],
  sheet: ['spreadsheet'],
  ring: ['call'],
  rang: ['call'],
  dial: ['call'],
  dialer: ['call'],
  dialler: ['call'],
  mail: ['email'],
  msg: ['message', 'chat'],
  remove: ['delete'],
  erase: ['delete'],
  cancel: ['delete'],
  add: ['create', 'new'],
  make: ['create', 'new'],
  enquiry: ['lead'],
  inquiry: ['lead'],
  prospect: ['lead'],
  deal: ['opportunity'],
  firm: ['company'],
  business: ['company'],
  org: ['company'],
  pwd: ['password'],
  signin: ['login'],
  signout: ['logout'],
  notify: ['notification'],
  alarm: ['reminder', 'alert'],
  cant: ['cannot'],
  permissions: ['permission'],
};

/** Lowercase words, punctuation stripped. Used for both queries and keywords. */
export function tokenise(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 1);
}

/** Built once. The nav half is derived, so it stays in step with the sidebar. */
export const KNOWLEDGE: Answer[] = [...WRITTEN, ...fromNavigation()];

/** Offered on an empty conversation — the questions people actually open with. */
export const OPENERS = [
  'Where are my files?',
  // One of these is an instruction rather than a question, on purpose: it is
  // the only way somebody discovers the assistant can do as well as explain.
  'Remind me to send the report tomorrow',
  'How do I import leads?',
  'Why can I not delete this?',
  'Which version am I on?',
];
